import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FreshnessBadge from '../common/FreshnessBadge';
import { useAppTheme } from '../../hooks/useAppTheme';

function isNseOpen(): boolean {
  const now = new Date();
  const ist = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const h = ist.getHours(), m = ist.getMinutes();
  const total = h * 60 + m;
  const day = ist.getDay();
  if (day === 0 || day === 6) return false; // weekend
  return total >= 555 && total <= 930; // 9:15 to 15:30
}

interface Props {
  alertCount?: number;
  dataWarning?: string | null;
  hasCrypto?: boolean;
  computedAt?: string | Date | null;
  feedSource?: string;
}

const MarketStateStrip: React.FC<Props> = ({ alertCount = 0, dataWarning, hasCrypto = false, computedAt, feedSource }) => {
  const { C } = useAppTheme();
  const [nseOpen, setNseOpen] = useState(isNseOpen());
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => {
      setNseOpen(isNseOpen());
      setTime(new Date());
    }, 30000);
    return () => clearInterval(t);
  }, []);

  const istTime = time.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 0.75,
      bgcolor: 'background.paper',
      borderBottom: '1px solid',
      borderColor: 'divider',
      flexWrap: 'wrap'
    }}>
      {/* NSE market status */}
      <Chip
        size="small"
        icon={<FiberManualRecordIcon sx={{ fontSize: 10, color: `${nseOpen ? C.bull : C.bear} !important` }} />}
        label={nseOpen ? 'NSE OPEN' : 'NSE CLOSED'}
        sx={{
          bgcolor: nseOpen ? C.bullMuted : C.bearMuted,
          color: nseOpen ? C.bull : C.bear,
          fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5
        }}
      />

      {/* Crypto status — always open 24/7 */}
      {hasCrypto && (
        <Chip
          size="small"
          icon={<FiberManualRecordIcon sx={{ fontSize: 10, color: `${C.bull} !important` }} />}
          label="CRYPTO 24/7"
          sx={{ bgcolor: C.bullMuted, color: C.bull, fontWeight: 700, fontSize: '0.7rem', letterSpacing: 0.5 }}
        />
      )}

      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        IST {istTime}
      </Typography>

      {/* Data freshness */}
      {computedAt && (
        <FreshnessBadge computedAt={computedAt} isExchangeOpen={nseOpen} feedSource={feedSource} size="small" />
      )}

      {/* Alert count */}
      {alertCount > 0 && (
        <Chip
          size="small"
          label={`${alertCount} signals today`}
          sx={{ bgcolor: C.neutralMuted, color: C.neutral, fontSize: '0.7rem', fontWeight: 600 }}
        />
      )}

      {/* Data warning */}
      {dataWarning && (
        <Chip
          size="small"
          icon={<WarningAmberIcon sx={{ fontSize: 14, color: `${C.warn} !important` }} />}
          label={dataWarning}
          sx={{ bgcolor: C.warnMuted, color: C.warn, fontSize: '0.7rem', fontWeight: 600 }}
        />
      )}
    </Box>
  );
};

export default MarketStateStrip;
